import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { DetailDrawer } from "@/components/common/DetailDrawer";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { printPurchaseOrder } from "@/lib/poPdf";
import { GrnReceiveDialog } from "@/components/common/GrnReceiveDialog";
import { toast } from "sonner";
import { Plus, Trash2, Send, Mail, XCircle, Loader2, ShieldAlert, FileDown, PackageCheck } from "lucide-react";

const COLS = [
  { key: "poNumber", label: "PO #", type: "mono" },
  { key: "supplierName", label: "Supplier", type: "text" },
  { key: "status", label: "Status", type: "badge" },
  { key: "grandTotal", label: "Grand Total", type: "currency" },
  { key: "createdAt", label: "Created", type: "datetime" },
];

const emptyLine = () => ({ ingredientId: "", name: "", unit: "kg", qty: "", rate: "", gstPct: "" });

export default function PurchaseOrders() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useResource("/purchase-orders");
  const { data: supData } = useResource("/suppliers");
  const { data: ingData } = useResource("/ingredients");
  const { data: company } = useResource("/settings/company");
  const suppliers = Array.isArray(supData) ? supData : [];
  const ingredients = Array.isArray(ingData) ? ingData : [];

  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [grnOpen, setGrnOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(false);

  const [supplierId, setSupplierId] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);

  const rows = Array.isArray(data) ? data : [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["resource", "/purchase-orders"] });

  const resetForm = () => {
    setSupplierId(""); setPaymentTerms(""); setDeliveryDate(""); setNotes(""); setLines([emptyLine()]);
  };
  const openCreate = () => { resetForm(); setFormOpen(true); };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const pickIng = (i, id) => {
    const ing = ingredients.find((x) => x.id === id);
    setLine(i, { ingredientId: id, name: ing?.name || "", unit: ing?.consumptionUnit || ing?.unit || "kg", rate: ing?.rate || "" });
  };
  const lineAmount = (l) => (Number(l.qty) || 0) * (Number(l.rate) || 0);
  const subtotal = lines.reduce((s, l) => s + lineAmount(l), 0);

  const onSupplier = (id) => {
    setSupplierId(id);
    const s = suppliers.find((x) => x.id === id);
    if (s?.paymentTerms) setPaymentTerms(s.paymentTerms);
  };

  const validate = () => {
    if (!supplierId) { toast.error("Select a supplier"); return false; }
    const valid = lines.filter((l) => l.ingredientId && l.qty);
    if (valid.length === 0) { toast.error("Add at least one line item"); return false; }
    return true;
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    const s = suppliers.find((x) => x.id === supplierId);
    const payload = {
      supplierId,
      supplierName: s?.name || "",
      supplierGstin: s?.gstin || "",
      supplierAddress: s?.address || "",
      paymentTerms,
      notes: deliveryDate ? `${notes}${notes ? " | " : ""}Expected Delivery: ${deliveryDate}` : notes,
      items: lines.filter((l) => l.ingredientId && l.qty).map((l) => ({
        ingredientId: l.ingredientId, name: l.name, qty: Number(l.qty) || 0, unit: l.unit,
        rate: Number(l.rate) || 0, gstPct: Number(l.gstPct) || 0, amount: lineAmount(l),
      })),
    };
    try {
      await api.post("/purchase-orders", payload);
      toast.success("Purchase Order created (Draft)");
      setFormOpen(false);
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Create failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const doStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${selected.id}/status`, { status });
      toast.success(`PO ${status.toLowerCase()}`);
      setDrawerOpen(false);
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Action failed"));
    } finally { setBusy(false); }
  };

  const doEmail = async () => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${selected.id}/email`, { html: "" });
      toast.success("PO emailed to supplier");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Email failed"));
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setCancelOpen(false);
    setBusy(true);
    try {
      await api.delete(`/purchase-orders/${selected.id}`);
      toast.success("PO cancelled");
      setDrawerOpen(false);
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Cancel failed"));
    } finally { setBusy(false); }
  };

  const doPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/purchase-orders/${selected.id}`);
      const ok = printPurchaseOrder(res.data, company || {});
      if (!ok) toast.error("Allow pop-ups to download the PO PDF");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not open PO"));
    } finally { setBusy(false); }
  };

  const canReceive = ["SENT", "APPROVED", "PARTIALLY_RECEIVED"].includes(String(selected?.status || "").toUpperCase());
  const status = String(selected?.status || "").toUpperCase();
  const actions = selected && (
    <>
      {status === "DRAFT" && (
        <Button data-testid="po-action-send" size="sm" className="gap-1.5 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={() => doStatus("SENT")}>
          <Send className="h-3.5 w-3.5" /> Send to Supplier
        </Button>
      )}
      <Button data-testid="po-action-pdf" size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={doPdf}>
        <FileDown className="h-3.5 w-3.5" /> Download PDF
      </Button>
      {canReceive && (
        <Button data-testid="po-action-receive" size="sm" variant="outline" className="gap-1.5 text-emerald-700" disabled={busy} onClick={() => setGrnOpen(true)}>
          <PackageCheck className="h-3.5 w-3.5" /> Receive / GRN
        </Button>
      )}
      <Button data-testid="po-action-email" size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={doEmail}>
        <Mail className="h-3.5 w-3.5" /> Email PO
      </Button>
      {status !== "CANCELLED" && (
        <Button data-testid="po-action-cancel" size="sm" variant="outline" className="gap-1.5 text-rose-600" disabled={busy} onClick={() => setCancelOpen(true)}>
          <XCircle className="h-3.5 w-3.5" /> Cancel
        </Button>
      )}
      {busy && <Loader2 className="h-4 w-4 animate-spin text-amber-700" />}
    </>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Purchase Orders" subtitle="Raise POs against suppliers — live in the shared ERP. Creating a PO does not create stock.">
        <Button data-testid="btn-new-purchase-order" onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800">
          <Plus className="h-4 w-4" /> New Purchase Order
        </Button>
      </PageHeader>

      {isLoading ? <LoadingState label="Loading purchase orders…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        rows.length === 0 ? (
          <EmptyState title="No purchase orders yet" message="Create a PO to order raw materials from a supplier."
            action={<Button data-testid="empty-new-po" onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4" /> New Purchase Order</Button>} />
        ) : (
          <DataTable rows={rows} columns={COLS} searchKeys={["poNumber", "supplierName"]} filters={[{ key: "status", label: "Status" }]} onRowClick={(r) => { setSelected(r); setDrawerOpen(true); }} testIdPrefix="po-row" />
        )}

      <DetailDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title="Purchase Order" row={selected} actions={actions} />

      <GrnReceiveDialog open={grnOpen} onOpenChange={setGrnOpen} po={selected} onDone={invalidate} />

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">New Purchase Order</DialogTitle>
            <DialogDescription className="text-xs">Saved as Draft in the live ERP. Stock is only affected later via GRN receipt + QA.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Supplier <span className="text-rose-500">*</span></Label>
              <Select value={supplierId} onValueChange={onSupplier}>
                <SelectTrigger data-testid="po-supplier" className="h-9 text-sm"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent className="max-h-60 bg-white">
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Terms</Label>
              <Input data-testid="po-terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Expected Delivery</Label>
              <Input data-testid="po-delivery" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes / Terms</Label>
              <Textarea data-testid="po-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[60px] text-sm" />
            </div>
          </div>

          <div className="rounded-lg border border-stone-200">
            <div className="flex items-center justify-between border-b border-stone-200 bg-stone-50 px-4 py-2">
              <span className="text-sm font-semibold text-stone-800">Line Items</span>
              <Button data-testid="po-add-line" size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setLines((l) => [...l, emptyLine()])}><Plus className="h-3.5 w-3.5" /> Add</Button>
            </div>
            <div className="divide-y divide-stone-100">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2 px-3 py-2">
                  <div className="col-span-4">
                    <Select value={l.ingredientId} onValueChange={(v) => pickIng(i, v)}>
                      <SelectTrigger data-testid={`po-line-ing-${i}`} className="h-9 text-sm"><SelectValue placeholder="Material" /></SelectTrigger>
                      <SelectContent className="max-h-60 bg-white">{ingredients.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input data-testid={`po-line-qty-${i}`} className="col-span-2 h-9 text-sm" type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                  <div className="col-span-1 text-xs text-stone-500">{l.unit}</div>
                  <Input data-testid={`po-line-rate-${i}`} className="col-span-2 h-9 text-sm" type="number" placeholder="Rate" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} />
                  <Input data-testid={`po-line-gst-${i}`} className="col-span-1 h-9 text-sm" type="number" placeholder="GST%" value={l.gstPct} onChange={(e) => setLine(i, { gstPct: e.target.value })} />
                  <div className="col-span-1 text-right text-xs font-mono text-stone-700">{fmtCurrency(lineAmount(l))}</div>
                  <button data-testid={`po-line-remove-${i}`} className="col-span-1 text-stone-400 hover:text-rose-600" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t border-stone-200 bg-stone-50 px-4 py-2 text-sm">
              <span className="text-stone-500">Subtotal:&nbsp;</span>
              <span data-testid="po-subtotal" className="font-mono font-semibold text-stone-900">{fmtCurrency(subtotal)}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button data-testid="po-save" className="bg-amber-700 hover:bg-amber-800" disabled={submitting} onClick={() => validate() && setConfirmOpen(true)}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Confirm write to live ERP</AlertDialogTitle>
            <AlertDialogDescription>This creates a Draft Purchase Order in the shared production database. It will NOT create stock. Proceed?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="po-confirm" className="bg-amber-700 hover:bg-amber-800" onClick={handleConfirm}>Yes, create</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this PO?</AlertDialogTitle>
            <AlertDialogDescription>This cancels the purchase order in the live ERP.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doCancel}>Cancel PO</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
